import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Modal } from '../components/Modal';
import { useAuth } from '../hooks/useAuth';
import { varinymAPI } from '../lib/varinym-api';
import type { DsrEraseResult } from '../types';

const TABS = [
  { path: '/dsr', label: 'Export / Erase' },
  { path: '/dsr/history', label: 'Request History' },
] as const;

const HISTORY_PAGE_SIZE = 20;

export function DataRequests() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Data Requests</h1>

      {!isAdmin ? (
        <Card>
          <p className="text-sm text-muted-foreground">
            Data subject requests (export and erasure) are restricted to admins and owners on this account.
          </p>
        </Card>
      ) : (
        <>
          {/* Tabs */}
          <div className="border-b border-border mb-6 overflow-x-auto overflow-y-hidden">
            <nav className="-mb-px flex space-x-8 min-w-max">
              {TABS.map((tab) => (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`link-bare ${
                    location.pathname === tab.path
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
          </div>

          {location.pathname === '/dsr/history' ? <AuditHistory /> : <ExportErase />}
        </>
      )}
    </div>
  );
}

function ExportErase() {
  const [userId, setUserId] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [showEraseModal, setShowEraseModal] = useState(false);
  const [eraseResult, setEraseResult] = useState<DsrEraseResult | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dsr-export', searchUserId],
    queryFn: () => varinymAPI.exportUserData(searchUserId),
    enabled: !!searchUserId,
    retry: false,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (userId.trim()) {
      setEraseResult(null);
      setSearchUserId(userId.trim());
    }
  };

  const handleErased = (result: DsrEraseResult) => {
    setEraseResult(result);
    setShowEraseModal(false);
  };

  const handleDownloadExport = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dsr-export-${searchUserId}-${data.exported_at}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const found = !!data?.found;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card title="Look Up User Data">
          <form onSubmit={handleSearch}>
            <div className="space-y-4">
              <Input
                label="User ID"
                placeholder="user@example.com"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                helperText="Enter the user ID to export or erase everything held for them"
              />
              <Button type="submit" className="w-full" isLoading={isLoading} disabled={!userId.trim()}>
                Look Up
              </Button>
            </div>
          </form>
        </Card>

        {searchUserId && isError && (
          <Card title="No Data Found">
            <p className="text-sm text-muted-foreground">No identity found for this user.</p>
          </Card>
        )}

        {searchUserId && found && data && (
          <>
            <Card
              title="Identity"
              actions={
                <Button variant="secondary" size="sm" onClick={handleDownloadExport}>
                  Download Export
                </Button>
              }
            >
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">User ID</dt>
                  <dd className="mt-1 text-sm text-foreground font-mono break-all">{data.identity.user_id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Record ID</dt>
                  <dd className="mt-1 text-sm text-foreground font-mono break-all">{data.identity.record_id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                  <dd className="mt-1 text-sm text-foreground">{new Date(data.identity.created).toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
                  <dd className="mt-1 text-sm text-foreground">{new Date(data.identity.updated).toLocaleString()}</dd>
                </div>
              </dl>
            </Card>

            <Card
              title={`Aliases (${data.aliases.length})`}
              actions={
                <Link to={`/dsr/history?user_id=${encodeURIComponent(searchUserId)}`} className="link-bare">
                  <span className="text-sm text-primary hover:underline">View DSR History →</span>
                </Link>
              }
            >
              {data.aliases.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Alias ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Context
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Expires
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.aliases.map((d) => (
                        <tr key={d.alias_id} className="hover:bg-accent/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                            {d.alias_id.slice(0, 12)}...
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{d.context}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                d.status === 'active' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
                              }`}
                            >
                              {d.status === 'active' ? 'Active' : 'Expired'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {d.expires === 0 ? 'Never' : new Date(d.expires * 1000).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No aliases for this user</p>
              )}
            </Card>

            <Card title="Erase User Data">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Permanently deletes the identity record and all {data.aliases.length} alias
                  {data.aliases.length === 1 ? '' : 's'} for this user. This cannot be undone.
                </p>
                <Button variant="danger" onClick={() => setShowEraseModal(true)}>
                  Erase All Data for This User
                </Button>
              </div>
            </Card>
          </>
        )}

        {eraseResult && (
          <Card title="Erasure Complete">
            <div className="bg-muted p-4 rounded-lg">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Records Deleted</dt>
                  <dd className="mt-1 text-lg font-semibold text-foreground">{eraseResult.records_deleted}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Aliases Deleted</dt>
                  <dd className="mt-1 text-lg font-semibold text-foreground">{eraseResult.aliases_deleted}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Erased At</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {new Date(eraseResult.erased_at * 1000).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>
          </Card>
        )}
      </div>

      <div>
        <Card title="Right of Access & Erasure">
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Export returns everything held for a user (GDPR Article 15). Erase permanently deletes it
              (GDPR Article 17) — there is no undo and no masked placeholder left behind.
            </p>
            <div className="pt-3 border-t border-border">
              <p className="font-medium text-card-foreground mb-2">Recommended flow:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Look up the user to review what they have first</li>
                <li>Erase only after confirming with the requester</li>
                <li>Every export and erase call is logged in Request History</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {found && data && (
        <EraseConfirmModal
          isOpen={showEraseModal}
          onClose={() => setShowEraseModal(false)}
          userId={searchUserId}
          aliasCount={data.aliases.length}
          onErased={handleErased}
        />
      )}
    </div>
  );
}

function EraseConfirmModal({
  isOpen,
  onClose,
  userId,
  aliasCount,
  onErased,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  aliasCount: number;
  onErased: (result: DsrEraseResult) => void;
}) {
  const [typedUserId, setTypedUserId] = useState('');

  const eraseMutation = useMutation({
    mutationFn: () => varinymAPI.eraseUserData(userId),
    onSuccess: (response) => {
      onErased(response);
      setTypedUserId('');
    },
  });

  const handleClose = () => {
    setTypedUserId('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Erase User Data">
      <div className="space-y-4">
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-destructive text-xl">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-destructive">This cannot be undone</h3>
              <p className="mt-1 text-sm text-destructive/80">
                This permanently deletes the identity record and all {aliasCount} alias
                {aliasCount === 1 ? '' : 's'} for <span className="font-mono">{userId}</span>, in one
                atomic batch. There is no recovery and no masked placeholder left behind.
              </p>
            </div>
          </div>
        </div>

        <Input
          label={`Type "${userId}" to confirm`}
          value={typedUserId}
          onChange={(e) => setTypedUserId(e.target.value)}
          placeholder={userId}
          autoComplete="off"
        />

        {eraseMutation.isError && (
          <p className="text-sm text-destructive">
            {(eraseMutation.error as Error)?.message || 'Erasure failed'}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={eraseMutation.isPending} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => eraseMutation.mutate()}
            isLoading={eraseMutation.isPending}
            disabled={typedUserId !== userId}
            className="flex-1"
          >
            Erase Permanently
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AuditHistory() {
  const [searchParams] = useSearchParams();
  const [userIdFilter, setUserIdFilter] = useState(searchParams.get('user_id') || '');
  const [appliedFilter, setAppliedFilter] = useState(searchParams.get('user_id') || '');
  const [offset, setOffset] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['dsr-audit', appliedFilter, offset],
    queryFn: () => varinymAPI.getDsrAudit({ userId: appliedFilter || undefined, limit: HISTORY_PAGE_SIZE, offset }),
  });

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setAppliedFilter(userIdFilter.trim());
  };

  const handleClearFilter = () => {
    setUserIdFilter('');
    setAppliedFilter('');
    setOffset(0);
  };

  const auditLog = data?.audit_log ?? [];
  const hasNextPage = auditLog.length === HISTORY_PAGE_SIZE;

  return (
    <div className="space-y-6">
      <Card>
        <form onSubmit={handleFilter} className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Input
              label="Filter by User ID"
              placeholder="user@example.com"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit">Filter</Button>
            {appliedFilter && (
              <Button type="button" variant="secondary" onClick={handleClearFilter}>
                Clear
              </Button>
            )}
          </div>
        </form>
      </Card>

      <Card title={appliedFilter ? `DSR History for ${appliedFilter}` : 'DSR History'}>
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : auditLog.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Performed At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Request Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      API Key
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Records Deleted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Contexts
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {auditLog.map((entry) => (
                    <tr key={entry.id} className="hover:bg-accent/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {new Date(entry.performed_at * 1000).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                        {entry.user_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            entry.request_type === 'erasure'
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-info/10 text-info'
                          }`}
                        >
                          {entry.request_type === 'erasure' ? 'Erasure' : 'Access'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-muted-foreground">
                        {entry.api_key_id}
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm font-mono text-muted-foreground"
                        title={entry.actor ? undefined : 'Not yet available for console-routed requests'}
                      >
                        {entry.actor ?? '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {entry.records_deleted ?? '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground max-w-xs truncate" title={entry.contexts?.join(', ')}>
                        {entry.contexts?.join(', ') ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center pt-4">
              <Button variant="secondary" size="sm" onClick={() => setOffset(Math.max(0, offset - HISTORY_PAGE_SIZE))} disabled={offset === 0}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">Showing {offset + 1}–{offset + auditLog.length}</span>
              <Button variant="secondary" size="sm" onClick={() => setOffset(offset + HISTORY_PAGE_SIZE)} disabled={!hasNextPage}>
                Next
              </Button>
            </div>
          </>
        ) : (
          <p className="text-center py-12 text-muted-foreground">
            {appliedFilter ? 'No DSR history for this user' : 'No DSR requests yet'}
          </p>
        )}
      </Card>
    </div>
  );
}
